/**
 * ChannelPicker Component
 * Apple-style visual notification channel selector with toggles
 */

import { Bell, BellOff, Smartphone } from 'lucide-react';
import clsx from 'clsx';
import type { Channel } from '../types';

interface ChannelOption {
  id: Channel;
  name: string;
  description: string;
  icon: React.ReactNode;
  capabilities: string[];
  preview: React.ReactNode;
}

const CHANNEL_OPTIONS: ChannelOption[] = [
  {
    id: 'push',
    name: 'Push 推送',
    description: '系统级推送通知，用户离线也能收到',
    icon: <Bell className="w-5 h-5" />,
    capabilities: ['标题', '正文', '点击跳转', '声音'],
    preview: (
      <div className="bg-gray-100 rounded-xl p-3 max-w-[280px]">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center text-white text-lg shrink-0">
            🔮
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">DORA</span>
              <span className="text-[10px] text-gray-400">现在</span>
            </div>
            <p className="text-xs font-semibold text-gray-900 truncate">通知标题</p>
            <p className="text-xs text-gray-600 line-clamp-2">通知内容会显示在这里...</p>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 'push_silent',
    name: '静默推送',
    description: '后台静默更新，用户无感知',
    icon: <BellOff className="w-5 h-5" />,
    capabilities: ['后台数据', '无界面'],
    preview: (
      <div className="bg-gray-100 rounded-xl p-3 max-w-[280px] opacity-60">
        <div className="flex items-center gap-2 text-gray-500">
          <span className="text-base">📡</span>
          <span className="text-xs">后台静默更新（用户不可见）</span>
        </div>
      </div>
    ),
  },
  {
    id: 'realtime',
    name: '应用内通知',
    description: '应用内 NotificationBar，需要用户在线',
    icon: <Smartphone className="w-5 h-5" />,
    capabilities: ['正文', '图标', '操作按钮'],
    preview: (
      <div className="bg-green-50 border border-green-200 rounded-lg p-2 max-w-[280px] flex items-center gap-2">
        <span className="text-base">✨</span>
        <span className="text-xs text-gray-700 flex-1">今日幸运符已生成</span>
        <span className="text-xs text-green-600 underline cursor-pointer">查看</span>
        <span className="text-gray-400 cursor-pointer">✕</span>
      </div>
    ),
  },
];

interface ChannelPickerProps {
  value: Channel[];
  onChange: (channels: Channel[]) => void;
  disabled?: boolean;
  className?: string;
}

export function ChannelPicker({ value, onChange, disabled, className }: ChannelPickerProps) {
  const toggleChannel = (channelId: Channel) => {
    if (disabled) return;

    if (value.includes(channelId)) {
      onChange(value.filter(c => c !== channelId));
    } else {
      onChange([...value, channelId]);
    }
  };

  return (
    <div className={clsx('space-y-3', className)}>
      <label className="block text-sm font-medium text-gray-700">
        选择通知渠道
      </label>

      <div className="space-y-3">
        {CHANNEL_OPTIONS.map(channel => {
          const isSelected = value.includes(channel.id);

          return (
            <div
              key={channel.id}
              className={clsx(
                'border-2 rounded-xl transition-all cursor-pointer',
                isSelected
                  ? 'border-indigo-500 bg-indigo-50/50'
                  : 'border-gray-200 hover:border-gray-300 bg-white',
                disabled && 'opacity-60 cursor-not-allowed'
              )}
              onClick={() => toggleChannel(channel.id)}
            >
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <div className={clsx(
                      'p-2 rounded-lg',
                      isSelected ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-500'
                    )}>
                      {channel.icon}
                    </div>

                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold text-gray-900">
                        {channel.name}
                      </h4>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {channel.description}
                      </p>

                      {/* Capabilities */}
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {channel.capabilities.map(cap => (
                          <span
                            key={cap}
                            className="inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded-full bg-gray-100 text-gray-600"
                          >
                            {cap}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Toggle Switch */}
                  <button
                    type="button"
                    role="switch"
                    aria-checked={isSelected}
                    disabled={disabled}
                    onClick={e => {
                      e.stopPropagation();
                      toggleChannel(channel.id);
                    }}
                    className={clsx(
                      'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2',
                      isSelected ? 'bg-indigo-600' : 'bg-gray-200',
                      disabled && 'cursor-not-allowed'
                    )}
                  >
                    <span
                      className={clsx(
                        'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition-transform',
                        isSelected ? 'translate-x-5' : 'translate-x-0'
                      )}
                    />
                  </button>
                </div>

                {/* Preview - shown when selected */}
                {isSelected && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-[10px] uppercase tracking-wide text-gray-400 mb-2">预览效果</p>
                    {channel.preview}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
